import { useCallback, useEffect, useState } from "react";

import { Badge, Button, Icons, Skeleton } from "../../../shared/ui";
import { AdminListRow } from "../AdminListRow";
import type { AuditFilter } from "../adminRoute";
import { ErrorRetry } from "../ErrorRetry";
import { OperatorDenied, OperatorRefused } from "../operatorHttp";
import { PermissionDenied } from "../PermissionDenied";
import { isSessionExpired, useReportSessionExpired } from "../sessionContext";
import { formatDateTime } from "../users/format";
import type { AuditApi, AuditEventItem } from "./auditApi";
import { actorLabel, outcomeLabel, outcomeTone } from "./auditFormat";
import { eventTypeLabel, useAuditT } from "./useAuditT";

type Status = "loading" | "ready" | "failed" | "denied";

/** Cursor-paginated list of audit events for one filter. */
export function AuditList({
  api,
  filter,
  selectedId,
  hrefFor,
  onOpen,
}: {
  api: AuditApi;
  filter: AuditFilter;
  selectedId: string | null;
  hrefFor: (eventId: string) => string;
  onOpen: (eventId: string) => void;
}) {
  const t = useAuditT();
  const reportExpired = useReportSessionExpired();
  const [events, setEvents] = useState<AuditEventItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [invalid, setInvalid] = useState<string | null>(null);
  const filterKey = JSON.stringify(filter);

  const fetchPage = useCallback(
    (after: string | null, active: { current: boolean }) => {
      api
        .list({ filter: JSON.parse(filterKey) as AuditFilter, cursor: after })
        .then((page) => {
          if (!active.current) return;
          setEvents((current) =>
            after === null ? page.items : [...current, ...page.items],
          );
          setCursor(page.next_cursor);
          setInvalid(null);
          setStatus("ready");
        })
        .catch((error: unknown) => {
          if (!active.current) return;
          if (isSessionExpired(error)) reportExpired();
          if (
            error instanceof OperatorRefused &&
            error.code.startsWith("audit_invalid")
          ) {
            setInvalid(error.code);
            setStatus("ready");
            return;
          }
          setStatus(error instanceof OperatorDenied ? "denied" : "failed");
        });
    },
    [api, filterKey, reportExpired],
  );

  useEffect(() => {
    const active = { current: true };
    fetchPage(null, active);
    return () => {
      active.current = false;
    };
  }, [fetchPage]);

  if (status === "denied") {
    return <PermissionDenied message={t("admin.denied.audit")} />;
  }
  const invalidMessage =
    invalid === "audit_invalid_cursor"
      ? t("admin.audit.filter.invalidCursor")
      : t("admin.audit.filter.invalid");

  return (
    <div className="flex flex-col gap-3">
      {invalid !== null && (
        <p role="alert" className="text-footnote text-error">
          {invalidMessage}
        </p>
      )}
      {status === "failed" && (
        <ErrorRetry
          message={t("admin.audit.failed")}
          onRetry={() => {
            setStatus("loading");
            fetchPage(null, { current: true });
          }}
        />
      )}
      {status === "loading" && events.length === 0 && (
        <div aria-busy="true">
          <Skeleton height={56} />
          <span className="sr-only">{t("admin.audit.loading")}</span>
        </div>
      )}
      {status === "ready" && invalid === null && events.length === 0 && (
        <p className="text-body text-fg-muted">{t("admin.audit.empty")}</p>
      )}
      {events.length > 0 && (
        <ul
          aria-label={t("admin.audit.list.caption")}
          className="divide-y divide-line-subtle overflow-hidden rounded-xl bg-raised shadow-card"
        >
          {events.map((row) => (
            <li key={row.event_id}>
              <AuditRow
                row={row}
                selected={row.event_id === selectedId}
                href={hrefFor(row.event_id)}
                onOpen={onOpen}
              />
            </li>
          ))}
        </ul>
      )}
      {cursor !== null && status === "ready" && (
        <Button
          variant="secondary"
          onClick={() => {
            setStatus("loading");
            fetchPage(cursor, { current: true });
          }}
        >
          {t("admin.audit.loadMore")}
        </Button>
      )}
    </div>
  );
}

function AuditRow({
  row,
  selected,
  href,
  onOpen,
}: {
  row: AuditEventItem;
  selected: boolean;
  href: string;
  onOpen: (eventId: string) => void;
}) {
  const t = useAuditT();
  return (
    <AdminListRow
      href={href}
      selected={selected}
      onSelect={() => {
        onOpen(row.event_id);
      }}
    >
      <span className="flex min-w-0 items-center justify-between gap-2">
        <span className="truncate text-callout font-semibold text-fg">
          {eventTypeLabel(t, row.event_type)}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          <Badge tone={outcomeTone(row.outcome)}>
            {outcomeLabel(t, row.outcome)}
          </Badge>
          <Icons.ChevronRight size={16} className="text-fg-subtle" />
        </span>
      </span>
      <span className="truncate text-caption text-fg-muted">
        {`${formatDateTime(row.occurred_at)} · ${actorLabel(t, row.actor)}`}
      </span>
    </AdminListRow>
  );
}
